"use client"

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { ChevronDownIcon } from "lucide-react"
import { type ComponentProps, useState } from "react"
import { toast } from "sonner"
import { applyCartCoupon, getCartItems, removeCartCoupon } from "@/api/cart"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { cn } from "@/utilities"

interface CartCouponSectionProps extends ComponentProps<"section"> {}

export const CartCouponSection = ({
  className,
  ...props
}: CartCouponSectionProps) => {
  const queryClient = useQueryClient()
  const [couponCode, setCouponCode] = useState("")
  const cartQuery = useQuery({
    queryKey: ["cart", "items"],
    queryFn: getCartItems,
    retry: false,
  })

  const coupons = cartQuery.data?.coupons ?? []

  const applyCouponMutation = useMutation({
    mutationFn: applyCartCoupon,
    onMutate: () => {
      const toastId = toast.loading("Aplicando cupom...")
      return { toastId }
    },
    onError: (_error, _variables, context) => {
      toast.error("Nao foi possivel aplicar o cupom.", {
        id: context?.toastId,
      })
    },
    onSuccess: (response, _variables, context) => {
      if (!response.ok) {
        toast.error(response.message?.trim() || "Cupom invalido.", {
          id: context?.toastId,
        })

        return
      }

      toast.success(response.message?.trim() || "Cupom aplicado com sucesso.", {
        id: context?.toastId,
      })
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ["cart", "items"] })
      await queryClient.invalidateQueries({
        queryKey: ["checkout", "order-review"],
      })
    },
  })

  const removeCouponMutation = useMutation({
    mutationFn: removeCartCoupon,
    onMutate: () => {
      const toastId = toast.loading("Removendo cupom...")
      return { toastId }
    },
    onError: (_error, _variables, context) => {
      toast.error("Nao foi possivel remover o cupom.", {
        id: context?.toastId,
      })
    },
    onSuccess: (response, _variables, context) => {
      if (!response.ok) {
        toast.error(
          response.message?.trim() || "Nao foi possivel remover o cupom.",
          {
            id: context?.toastId,
          }
        )

        return
      }

      toast.success(response.message?.trim() || "Cupom removido.", {
        id: context?.toastId,
      })
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ["cart", "items"] })
      await queryClient.invalidateQueries({
        queryKey: ["checkout", "order-review"],
      })
    },
  })

  const applyCoupon = () => {
    const normalizedCode = couponCode.trim()

    if (!normalizedCode || applyCouponMutation.isPending) {
      return
    }

    applyCouponMutation.mutate({ code: normalizedCode })
    setCouponCode("")
  }

  return (
    <section
      className={cn(
        "space-y-4 rounded-2xl border bg-white p-5 shadow-xs",
        className
      )}
      {...props}
    >
      <button
        className='flex w-full items-center justify-between text-left font-semibold text-xl'
        type='button'
      >
        Desconto do Cupom
        <ChevronDownIcon aria-hidden='true' size={18} />
      </button>
      <div className='space-y-3'>
        <Input
          aria-label='Codigo do cupom'
          className='h-10 rounded-md'
          onChange={(event) => {
            setCouponCode(event.target.value)
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault()
              applyCoupon()
            }
          }}
          placeholder='Codigo do Cupom'
          value={couponCode}
        />
        <Button
          disabled={
            applyCouponMutation.isPending || couponCode.trim().length === 0
          }
          onClick={applyCoupon}
          type='button'
          variant='outline'
        >
          {applyCouponMutation.isPending ? "Aplicando..." : "Aplicar Cupom"}
        </Button>

        {coupons.length > 0 ? (
          <ul className='space-y-2'>
            {coupons.map((coupon) => (
              <li
                className='flex items-center justify-between rounded-md border bg-muted/40 px-3 py-2'
                key={coupon.code}
              >
                <span className='font-medium text-sm'>{coupon.code}</span>
                <Button
                  disabled={removeCouponMutation.isPending}
                  onClick={() => {
                    removeCouponMutation.mutate({ code: coupon.code })
                  }}
                  size='sm'
                  type='button'
                  variant='ghost'
                >
                  Remover
                </Button>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </section>
  )
}
